import { type DataExport } from '@hvac/contracts';
import { Alert, Button, Card, CardBody, CardHeader, Pill } from '@hvac/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import {
  dataExportPartUrl,
  dataExportsQuery,
  requestDataExport,
} from '../../shared/api/data-export-queries.js';

/**
 * Everything the business owns, downloadable: the books as CSV and every
 * receipt as the original file. Built in the background, kept seven days.
 */
export function ExportDataCard({ organizationId }: { organizationId: string }) {
  const queryClient = useQueryClient();
  const exports = useQuery(dataExportsQuery(organizationId));
  const [error, setError] = useState<string | null>(null);

  const request = useMutation({
    mutationFn: () => requestDataExport(organizationId),
    onSuccess: () => {
      setError(null);
      void queryClient.invalidateQueries({ queryKey: ['data-exports', organizationId] });
    },
    onError: (caught) =>
      setError(caught instanceof Error ? caught.message : 'The export was not started.'),
  });

  const download = async (item: DataExport, index: number) => {
    try {
      const { url } = await dataExportPartUrl(organizationId, item.id, index);
      window.location.assign(url);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'That part could not be downloaded.');
    }
  };

  // Year-end packages live on the Books page beside the year they belong to.
  const rows = (exports.data ?? []).filter(
    (item) => item.status !== 'expired' && item.kind === 'everything',
  );
  const inFlight = rows.some((item) => item.status === 'queued' || item.status === 'building');

  return (
    <Card>
      <CardHeader
        title="Export everything"
        description="The books as spreadsheets, and every receipt as its original file. Yours to keep, to give an accountant, or to take elsewhere."
      />
      <CardBody className="flex flex-col gap-3">
        {error === null ? null : <Alert tone="danger">{error}</Alert>}
        <div>
          <Button
            onClick={() => request.mutate()}
            isLoading={request.isPending}
            disabled={inFlight}
          >
            {inFlight ? 'Building your export…' : 'Export everything'}
          </Button>
        </div>
        {rows.map((item) => (
          <div
            key={item.id}
            className="flex flex-col gap-2 rounded-[var(--radius-control)] border border-border px-4 py-3"
          >
            <div className="flex items-center gap-2 text-sm">
              <span>{new Date(item.createdAt).toLocaleString('en-CA')}</span>
              <Pill
                tone={
                  item.status === 'ready'
                    ? 'success'
                    : item.status === 'failed'
                      ? 'danger'
                      : 'neutral'
                }
              >
                {item.status === 'ready'
                  ? 'Ready'
                  : item.status === 'failed'
                    ? 'Failed'
                    : 'Building'}
              </Pill>
              {item.expiresAt === null ? null : (
                <span className="text-xs text-content-muted">
                  Kept until {new Date(item.expiresAt).toLocaleDateString('en-CA')}
                </span>
              )}
            </div>
            {item.status === 'failed' && item.error !== null ? (
              <p className="text-xs text-danger">{item.error}</p>
            ) : null}
            {item.status === 'ready' ? (
              <ul className="flex flex-col gap-1 text-sm">
                {item.parts.map((part, index) => (
                  <li key={part.name}>
                    <button
                      type="button"
                      className="text-brand-600 underline hover:text-brand-700"
                      onClick={() => void download(item, index)}
                    >
                      {part.name}
                    </button>{' '}
                    <span className="text-xs text-content-muted">
                      {(part.bytes / 1_048_576).toFixed(1)} MB
                    </span>
                  </li>
                ))}
              </ul>
            ) : null}
          </div>
        ))}
      </CardBody>
    </Card>
  );
}
